/**
 * English: React context for station list from API and the active station selection (shared UI state).
 * Thai: Context สำหรับรายการสถานีจาก API และสถานีที่เลือกใช้งาน (state ร่วมของ UI)
 */
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API_BASE, ENDPOINTS } from '../config/api';
import { Station } from '../types';
import { useAuth } from './AuthContext';
import { parseJsonResponse } from '../utils/httpJson';

/**
 * GET /api/v2/inventory/stations returns snake_case rows (`station_id`, `org_name`, …) without `id`.
 * Inventory routes expect the text `station_id` slug in paths and query params — not `station_db_id`.
 */
function normalizeInventoryStation(raw: Record<string, unknown>): Station {
  const slug = String(raw.station_id ?? '').trim();
  const dbId = String(raw.station_db_id ?? '').trim();
  const id = slug || dbId;
  const displayName = raw.name != null ? String(raw.name).trim() : '';
  return {
    ...raw,
    id,
    name: displayName || slug || id,
  };
}

interface StationContextValue {
  stations: Station[];
  activeStationId: string;
  setActiveStationId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
}

const StationContext = createContext<StationContextValue>({
  stations: [],
  activeStationId: '',
  setActiveStationId: () => { },
  loading: true,
  refresh: async () => { },
});

interface StationProviderProps {
  children?: ReactNode;
}

export function StationProvider({ children }: StationProviderProps) {
  const { selectedOrgName, token } = useAuth();
  const [stations, setStations] = useState<Station[]>([]);
  const [activeStationId, setActiveStationId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const refresh = useCallback(async () => {
    try {
      const url = new URL(`${API_BASE}${ENDPOINTS.stations}`, window.location.origin);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url.toString(), { headers });
      const data = await parseJsonResponse<{ stations?: Station[]; default?: string }>(res, {
        url: url.toString(),
      });
      if (!res.ok) {
        console.error(
          `Failed to load stations: HTTP ${res.status} ${res.statusText}`,
          data,
        );
        setStations([]);
        return;
      }
      const rawList = data.stations || [];
      const list = rawList.map((s) => normalizeInventoryStation(s as Record<string, unknown>));
      // Filter by selected chat org when set; otherwise show all enabled orgs' stations.
      const uo = (selectedOrgName || '').trim().toLowerCase();
      let filtered = list;
      if (uo) {
        filtered = list.filter((s: Station) => {
          const org = String((s as Station & { org_name?: string }).org_name ?? '')
            .trim()
            .toLowerCase();
          const sid = (s.id || '').trim().toLowerCase();
          const sname = (s.name || '').trim().toLowerCase();
          return org === uo || sid === uo || sname === uo;
        });
      }
      const seen = new Set<string>();
      const deduped = filtered.filter((s: Station) => {
        const id = (s.id || '').trim();
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setStations(deduped);
      setActiveStationId((prev) => {
        if (uo && deduped.length > 0) {
          const match = deduped.find((s: Station) => {
            const org = String((s as Station & { org_name?: string }).org_name ?? '')
              .trim()
              .toLowerCase();
            return org === uo || (s.id || '').trim().toLowerCase() === uo;
          });
          if (match?.id) return match.id.trim();
        }
        return (prev || deduped[0]?.id) ?? '';
      });
    } catch (err) {
      console.error('Failed to load stations:', err);
      setStations([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgName, token]);

  useEffect(() => { refresh(); }, [refresh]);

  // ถ้า refresh ตั้ง activeStationId ไม่ทัน / ค่าว่างค้าง — บังคับเลือกสถานีแรกเมื่อมีรายการ
  useEffect(() => {
    if (loading || stations.length === 0) return;
    setActiveStationId((prev) => prev || stations[0].id);
  }, [loading, stations]);

  return (
    <StationContext.Provider value={{ stations, activeStationId, setActiveStationId, loading, refresh }}>
      {children}
    </StationContext.Provider>
  );
}

export function useStation() {
  return useContext(StationContext);
}
